/**
 * Centralized localStorage keys
 *
 * Type-safe constants for all localStorage keys in the application.
 * Use these instead of magic strings to prevent typos and enable refactoring.
 *
 * Convention for new keys: name them `nx-<surface>-<name>` (e.g.
 * `nx-cue-preferences`), register them here, and read them through
 * `useLocalStorage(key, default, { schema })` with a Zod schema so stale or
 * corrupt storage falls back to the default instead of feeding an invalid
 * value downstream.
 */

export const STORAGE_KEYS = {
  // Dev mode
  DEV_MODE: "nx-dev-mode",
  ADMIN_DEBUG: "nx-admin-debug-mode",

  // My Tools
  MY_TOOLS_FILTERS: "my-tools-filters",

  // My Agents
  MY_AGENTS_FILTERS: "my-agents-filters",

  // Prompt editor
  PROMPT_JSON_COPY_INFO_SEEN: "nexus-prompt-json-copy-info-seen",
  PROMPT_AI_VIEW_PANEL_WIDTH: "nexus:prompt-ai-view-panel-width",

  // Cue panel
  CUE_PANEL: "nexus:cue-panel",
  CUE_PANEL_WIDTH: "nexus:cue-panel-width",
  // Sticky per-device chat preferences (selected model + permission mode).
  // Read via useLocalStorage with `ZCuePreferences`.
  CUE_PREFERENCES: "nx-cue-preferences",

  // Show Resource IDs (developer feature)
  SHOW_RESOURCE_IDS: "nx-show-resource-ids",

  // The selected Role — the second global scope, beside the organization.
  // Holds `{ organizationId, roleScopeId }`, never a bare id: a selection made in
  // one organization must not survive a switch into another. Read via
  // `useLocalStorage` with the schema in `RoleScopeProvider`, which drops a stale
  // or corrupt entry back to "All roles".
  ROLE_SCOPE: "nx-role-scope",

  // Which metrics the Role stat strip shows, per surface — a reader's view
  // preference over a screen, never data. One entry for every Role rather than
  // one per Role: an unevictable browser store should not grow with every Role
  // ever opened, and the metrics that matter belong to whoever is reading.
  // Read via `useLocalStorage` with the schema in `useMetricStrip`, which
  // clamps a stored selection back inside the strip's one-to-six limits.
  ROLE_METRIC_STRIP: "nx-role-metric-strip",

  // Playground chat UI toggles (persisted per browser)
  PLAYGROUND_SHOW_TOOL_CALLS: "playground:showToolCalls",
  PLAYGROUND_SHOW_THINKING: "playground:showThinking",

  // Admin layout
  ADMIN_SIDEBAR_WIDTH: "admin-sidebar-width",
  ADMIN_AGENT_PANEL_WIDTH: "admin-agent-panel-width",

  // Workflow webhook trigger onboarding
  WEBHOOK_TRIGGER_INFO_DISMISSED: "webhook-trigger-info-dismissed",
  NODE_CONFIG_PANEL_WIDTH: "workflow:node-config-panel-width",

  // OAuth (prefix for service-specific keys)
  OAUTH_PREFIX: "nx-oauth",
  // Where a tool's built-in OAuth sign-in returns the user, suffixed with the tool
  // id. Holds `{ from }`, an in-app path. Read and written only through
  // `common/hooks/tools/tool-oauth-return.ts`. The spelling predates the `nx-`
  // convention and stays: a renamed key would drop the return path of every
  // sign-in already in flight at the provider when it ships.
  TOOL_OAUTH_RETURN_PREFIX: "toolConfiguration_",

  // Active UI language. Owned by i18next's language detector — see
  // `src/i18n/index.ts`. An unsupported or corrupt value is filtered by
  // `supportedLngs` and degrades to `en`, so it can never blank the UI.
  //
  // 🚨 This one holds a RAW string (`fr`), NOT JSON. The detector writes it with
  // a bare `setItem`, so `storage.get`/`useLocalStorage` would try to
  // `JSON.parse` an unquoted value. Read it through i18next (`i18n.language`),
  // never through the storage service.
  LANGUAGE: "nx-i18n-language"
} as const

/**
 * Type for all valid storage keys
 */
export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS]
